/**
 * usePlan — convenience hook for checking plan limits defined in config.
 */

import { usePurchases } from '../contexts/PurchasesContext';
import { PLANBASE_CONFIG } from '../config';
import type { PlanTier } from '../lib/revenuecat';
import { useAuth } from '../contexts/AuthContext';
import { hasAdminAccess } from '../lib/admin';
import { resolveUserTier } from '../lib/plan';

export function usePlan() {
  const { plan, planLoading, offering, purchase, restore, refreshPlan, iapAvailable } = usePurchases();
  const { user } = useAuth();

  const isAdmin = hasAdminAccess(user?.id, user?.email);
  // Merge RevenueCat's mobile-IAP tier with the Supabase-side tier so web
  // Stripe payers are recognized alongside mobile payers. Whichever is
  // higher wins — a user with an unknown RevenueCat state but a paid
  // Supabase tier should still see paid features.
  const serverTier = resolveUserTier(user);
  const RANK: Record<PlanTier, number> = { free: 0, pro: 1, enterprise: 2 };
  const higher: PlanTier = RANK[serverTier] > RANK[plan.tier] ? serverTier : plan.tier;
  const tier: PlanTier = isAdmin ? 'enterprise' : higher;
  const limits = PLANBASE_CONFIG.planLimits[tier] ?? PLANBASE_CONFIG.planLimits.free;

  return {
    // State
    tier,
    plan,
    planLoading,
    iapAvailable,

    // Limits (resolved from config)
    maxProjects:         limits.maxProjects,
    maxImagesPerProject: limits.maxImagesPerProject,
    canViewUnlimited:    limits.canViewDiscovery === -1,
    discoveryLimit:      limits.canViewDiscovery,

    // Checks
    isPro:        tier === 'pro' || tier === 'enterprise',
    isEnterprise: tier === 'enterprise',
    isFree:       tier === 'free',
    isAdmin,

    canUpload:    tier !== 'free' || limits.maxProjects > 0,
    canAddMore:   (current: number) => limits.maxProjects === -1 || current < limits.maxProjects,

    // Actions
    offering,
    purchase,
    restore,
    refreshPlan,
  };
}
